import { PartialWithId } from '@inmagik/react-crud/dist/types'
import { FormikHelpers } from 'formik'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useAuth } from '../../auth'
import ModalForm from '../../components/ModalForm'
import {
  useAllegatiIntervento,
  useCreateIntervento,
  useIntervento,
  usePartialUpdateIntervento,
} from '../../hooks/interventi'
import { Intervento } from '../../types'
import {
  getLabelStatoIntervento
} from '../../utils/utils'
import FormProgrammaIntervento from '../BilanciaDettaglio/FormProgrammaIntervento'
import CardBilanciaInfo from './CardBilanciaInfo'
import CardFlussoRapportoTaratura from './CardFlussoRapportoTaratura'
import CardFlussoVerificazionePeriodica from './CardFlussoVerificazionePeriodica'
import CardPrenotazione from './CardPrenotazione'
import CardStoricoIntervento from './CardStoricoIntervento'
import FormAnnullaIntervento from './FormAnnullaIntervento'
import FormEsitoRiesame from './FormEsitoRiesame'
import FormNonEseguibile from './FormNonEseguibile'
import ModalConfirmIntervento from './ModalConfirmIntervento'

const initialValues = {
  data_intervento: '',
  note: '',
}

const schemaEsitoRiesame = yup.object().shape({
  esito_riesame: yup.string().required().label('Esito riesame'),
})

const schema = yup.object().shape({})

export default function InteventoDettaglio() {
  const { id } = useParams()
  const intervento = useIntervento(String(id))
  const { user } = useAuth()

  const { mutateAsync: updatePartialIntervento } = usePartialUpdateIntervento()
  const { mutateAsync: updatePartialInterventoJson } = usePartialUpdateIntervento('json')
  const { mutateAsync: createIntervento } = useCreateIntervento()

  const allegatiIntervento = useAllegatiIntervento({
    intervento: String(intervento?.data?.id),
  })
  const [modalStateConfirm, modalActionsConfirm] = useModalTrigger()
  const [modalStateAnnulla, modalActionsAnnulla] = useModalTrigger<Intervento>()
  const [modalStateUpdate, modalActionsUpdate] = useModalTrigger<Intervento>()
  const [modalStateModificaRiesame, modalActionsModificaRiesame] = useModalTrigger<Intervento>()
  const [modalStateNonEseguibile, modalActionsNonEseguibile] = useModalTrigger<Intervento>()
  const [isOpenEditAnnulla, setIsOpenEditAnnulla] = useState(false)

  const editAnnullaRef = useRef<HTMLDivElement>(null)

  const saveEsitoRiesame = useCallback(
    async (values: any, { setSubmitting }: FormikHelpers<any>) => {
      await updatePartialIntervento({
        id: intervento?.data?.id,
        esito_riesame: values.esito_riesame,
      }).then(() => {
        toast.success('Esito riesame impostato con successo', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        })
        intervento.refetch()
        modalActionsModificaRiesame.close()
      })
    },
    [intervento, updatePartialIntervento, modalActionsModificaRiesame]
  )

  const saveAnnullaIntervento = useCallback(
    async (values: any, { setSubmitting }: FormikHelpers<any>) => {
      await updatePartialIntervento({
        id: intervento?.data?.id,
        stato: 'annullato',
        note_annullamento: values.note_annullamento,
      }).then(() => {
        toast.success('Intervento annullato con successo', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        })
        intervento.refetch()
        modalActionsAnnulla.close()
      })
    },
    [intervento, updatePartialIntervento, modalActionsAnnulla]
  )

  const saveNonEseguibile = useCallback(
    async (values: any, { setSubmitting }: FormikHelpers<any>) => {
      await updatePartialIntervento({
        id: intervento?.data?.id,
        stato: 'non_eseguito',
        note: values.note,
      }).then(() => {
        toast.success('Intervento non eseguito con successo', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        })
        intervento.refetch()
        modalActionsNonEseguibile.close()
      })
    },
    [intervento, updatePartialIntervento, modalActionsNonEseguibile]
  )

  const saveProgrammaIntervento = useCallback(
    async (inter: PartialWithId<Intervento>, { setSubmitting }: FormikHelpers<any>) => {
      await updatePartialInterventoJson({
        id: Number(id!),
        data_intervento: inter.data_intervento,
        utente: inter.utente,
        note: inter.note,
        durata_stimata:
          // add 00 seconds to the time
          inter.durata_stimata?.length === 5 ? `${inter.durata_stimata}:00` : inter.durata_stimata,
      }).then(() => {
        toast.success('Intervento modificato con successo', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        })
        intervento.refetch()
        modalActionsUpdate.close()
      })
    },
    [intervento, updatePartialInterventoJson, modalActionsUpdate]
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editAnnullaRef.current && !editAnnullaRef.current.contains(event.target as Node)) {
        setIsOpenEditAnnulla(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editAnnullaRef])

  const router = useNavigate()

  if (intervento.isLoading) {
    return (
      <div className="page">
        <div className="h-100 d-flex justify-content-center align-items-center">
          <Spinner animation="border" role="status" variant="secondary" />
        </div>
      </div>
    )
  }

  return (
    <div className="page mb-5">
      <div className="row mt-4">
        <div className="col-12">
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                router(-1)
              }}
              className="p-sm no-link d-flex align-items-center pointer"
            >
              <span className="material-icons text-secondary">event_note</span>
              <span className="ms-2">Lista interventi</span>
            </div>
            <span className="material-icons text-secondary">chevron_right</span>
            <span className="ms-2 p-sm  bg-secondary-light p-2 text-secondary rounded">Dettaglio intervento</span>
          </div>
        </div>
        <div className="col-12 d-flex align-items-center mt-2">
          <h1 className="title">Intervento n° {id}</h1>
          {(intervento?.data?.stato === 'richiesto' || intervento?.data?.stato === 'programmato') && (
            <div className="ms-3">
              <Button
                onClick={() => {
                  setIsOpenEditAnnulla(!isOpenEditAnnulla)
                }}
                variant="light"
                className="text-secondary d-flex align-items-center"
                type="button"
              >
                <span className="material-icons">more_horiz</span>
              </Button>
              {isOpenEditAnnulla && (
                <div
                  className="list-group position-absolute"
                  ref={editAnnullaRef}
                  style={{
                    zIndex: 1000,
                    width: '200px',
                    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',

                    borderRadius: '5px',
                  }}
                >
                  {(intervento?.data?.stato === 'programmato' || intervento?.data?.stato === 'richiesto') && (
                    <button
                      onClick={() => {
                        modalActionsUpdate.open(intervento.data)
                      }}
                      className="list-group-item list-group-item-action d-flex align-items-center"
                    >
                      <span
                        className="material-icons me-2"
                        style={{
                          fontSize: '16px',
                        }}
                      >
                        edit
                      </span>
                      Modifica
                    </button>
                  )}
                  <button
                    onClick={() => {
                      modalActionsAnnulla.open(intervento.data)
                    }}
                    className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                  >
                    <span
                      className="material-icons me-2"
                      style={{
                        fontSize: '16px',
                      }}
                    >
                      delete
                    </span>
                    Annulla intervento
                  </button>
                  <button
                    onClick={() => {
                      modalActionsNonEseguibile.open(intervento.data)
                    }}
                    className="list-group-item list-group-item-action d-flex align-items-center"
                  >
                    <span
                      className="material-icons me-2"
                      style={{
                        fontSize: '16px',
                      }}
                    >
                      close
                    </span>
                    Non eseguibile
                  </button>
                </div>
              )}
            </div>
          )}
          {intervento?.data?.numero_pratica_esportazione && (
            <div className="badge bg-primary p-2 ms-3">
              Numero pratica: <span className="text-white fw-bold">{intervento.data.numero_pratica_esportazione}</span>
            </div>
          )}
        </div>
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-start">
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor:
                  intervento?.data?.stato === 'trasmesso' ||
                  (intervento?.data?.stato === 'eseguito' && intervento.data.tipo_intervento === 'rapporto_taratura')
                    ? '#198754'
                    : '#6c757d',
              }}
            ></div>
            <div className="ms-2">{getLabelStatoIntervento(intervento?.data?.stato)}</div>
          </div>
        </div>
      </div>
      {intervento.data?.stato === 'annullato' && (
        <div className="row mt-2">
          <div className="col-12">
            <div className="alert alert-danger mt-3" role="alert">
              <div className="heading-h4 text-danger">Intervento annullato</div>
              <div className="mt-3 text-black">
                <div className="label-m">Note annullamento</div>
                <div className="p-md mt-2">{intervento?.data?.note_annullamento || 'Nessuna nota'}</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {intervento.data?.stato === 'non_eseguito' && (
        <div className="row mt-2">
          <div className="col-12">
            <div className="alert alert-danger mt-3" role="alert">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="heading-h4 text-danger">Intervento non eseguito</div>
                  <div className="mt-3 text-black">
                    <div className="label-m">Note</div>
                    <div className="p-md mt-2">{intervento?.data?.note || 'Nessuna nota'}</div>
                  </div>
                </div>
                {(user?.role === 'supervisore' || user?.role === 'amministratore') && (
                  <Button
                    variant="outline-secondary"
                    className="text-secondary d-flex align-items-center bg-white"
                    onClick={() => {
                      if (!intervento.data) return
                      createIntervento({
                        bilancia: intervento.data.bilancia,
                        tipo_intervento: intervento.data.tipo_intervento,
                        numero_prenotazione: intervento.data.numero_prenotazione,
                        data_richiesta: intervento.data.data_richiesta,
                        stato: 'richiesto',
                      }).then((intervento: Intervento) => {
                        toast.success('Intervento ricreato con successo', {
                          position: 'bottom-right',
                          autoClose: 5000,
                          hideProgressBar: false,
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          progress: undefined,
                        })
                        router(`/interventi/${intervento.id}`)
                      })
                    }}
                  >
                    Ricrea intervento
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="row mt-2">
        {/* Su mobile la card del flusso (dove si lavora sull'intervento) viene prima */}
        <div className="col-md-5 order-2 order-md-1">
          <CardPrenotazione intervento={intervento} modalActionsUpdate={modalActionsUpdate} updatePartialInterventoJson={updatePartialInterventoJson} />
          <CardBilanciaInfo intervento={intervento} />
          <CardStoricoIntervento intervento={intervento.data} />
        </div>
        <div className="col-md-7 order-1 order-md-2">
          {intervento?.data?.tipo_intervento === 'verifica_periodica' && (
            <CardFlussoVerificazionePeriodica
              intervento={intervento}
              allegatiIntervento={allegatiIntervento}
              modalActionsConfirm={modalActionsConfirm}
              modalActionsModificaRiesame={modalActionsModificaRiesame}
            />
          )}
          {intervento?.data?.tipo_intervento === 'rapporto_taratura' && (
            <CardFlussoRapportoTaratura intervento={intervento} allegatiIntervento={allegatiIntervento} />
          )}
        </div>
      </div>
      {modalStateAnnulla.isOpen && (
        <ModalForm
          title="Annulla intervento"
          isOpen={modalStateAnnulla.isOpen}
          toggle={modalActionsAnnulla.close}
          save={saveAnnullaIntervento}
          initialValues={modalStateAnnulla.value ?? initialValues}
          schema={yup.object().shape({
            note_annullamento: yup.string().required().label('Note annullamento'),
          })}
        >
          <FormAnnullaIntervento intervento={modalStateAnnulla.value} />
        </ModalForm>
      )}
      {modalStateNonEseguibile.isOpen && (
        <ModalForm
          title="Intervento non eseguibile"
          isOpen={modalStateNonEseguibile.isOpen}
          toggle={modalActionsNonEseguibile.close}
          save={saveNonEseguibile}
          initialValues={modalStateNonEseguibile.value ?? initialValues}
          schema={yup.object().shape({
            note: yup.string(),
          })}
        >
          <FormNonEseguibile intervento={modalStateNonEseguibile.value} />
        </ModalForm>
      )}
      {modalStateModificaRiesame.isOpen && (
        <ModalForm
          title="Modifica riesaminazione"
          isOpen={modalStateModificaRiesame.isOpen}
          toggle={modalActionsModificaRiesame.close}
          save={saveEsitoRiesame}
          initialValues={modalStateModificaRiesame.value ?? initialValues}
          schema={schemaEsitoRiesame}
        >
          <FormEsitoRiesame intervento={modalStateModificaRiesame.value} />
        </ModalForm>
      )}

      {modalStateConfirm.isOpen && (
        <ModalConfirmIntervento
          onConfirm={async (data: Intervento) => {
            await updatePartialIntervento({
              id: data?.id,
              stato: 'riesaminato',
              note_intervento_invalido: data?.note,
            }).then(() => {
              toast.success('Intervento certificato con successo', {
                position: 'bottom-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
              })
              intervento.refetch()
            })
          }}
          isOpen={modalStateConfirm.isOpen}
          intervento={modalStateConfirm.value}
          toggle={modalActionsConfirm.close}
          title="Conferma riesaminazione"
          body={
            <div>
              <div>Vuoi confermare l’esito della riesaminazione come "{intervento.data?.esito_riesame}"</div>
              <div className="text-body-secondary">
                Potrai modifica questa scelta finchè l’intervento sarà nello stato “riesaminato”. Una volta che verrà
                inserito in una esportazione, la scelta sarà irreversibile.
              </div>
              {modalStateConfirm.value.data?.esito_riesame === 'invalido' && (
                <div className="p-sm mt-2">Puoi inserire qui sotto eventuali note/informazioni aggiuntive.</div>
              )}
            </div>
          }
        />
      )}
      {modalStateUpdate.isOpen && (
        <ModalForm<Intervento>
          title={
            modalStateUpdate.value?.tipo_intervento === 'verifica_periodica'
              ? 'Programma intervento: verifica periodica'
              : 'Programma intervento: taratura'
          }
          isOpen={modalStateUpdate.isOpen}
          toggle={modalActionsUpdate.toggle}
          initialValues={modalStateUpdate.value ?? initialValues}
          save={saveProgrammaIntervento}
          schema={schema}
          size="lg"
        >
          <FormProgrammaIntervento bilancia={modalStateUpdate.value?.bilancia_data} />
        </ModalForm>
      )}
    </div>
  )
}
